import React, { useContext, useEffect, useRef, useState } from 'react'
import Feed from 'feed-media-audio-player'

import { axios } from 'api'
import { useDebounce } from 'hooks'
import { isInRange } from 'helpers'
import { useVideoPlayerContext } from 'components_v2/VideoPlayer/VideoPlayerContext'

const Store = React.createContext()
export const useMusicContext = () => useContext(Store)

const MusicContextProvider = ({ musicEvents, children }) => {
  const { videoPlayer, state } = useVideoPlayerContext()

  const [musicPlayer, setMusicPlayer] = useState(null)
  const [shouldPlayMusic, setShouldPlayMusic] = useState()
  const [preferencesLoaded, setPreferencesLoaded] = useState(false) // we need to toggle this before rendering children, because shouldPlayMusic is tied to a controllable child. If we set it after rendering the component, we get an error and things go wonky.

  const [eventVolume, setEventVolume] = useState(1.0) // {float} - the desired volume ratio given to us from music events.
  const [userVolume, setUserVolume] = useState(
    parseInt(localStorage.getItem('musicVolume')) || 50
  )
  const debouncedUserVolume = useDebounce(userVolume, 150)
  const [stationId, setStationId] = useState(null)

  const [stationsObject, setStationsObject] = useState() // An object to hold the mapping between feedFM music ID (ephemeral) and station names assigned by us (permanent)
  const [currentSong, setCurrentSong] = useState()
  const [showMarquee, setShowMarquee] = useState(false)
  const [marqueeExiting, setMarqueeExiting] = useState(false)

  const chromeCastRef = useRef()

  /* Set user's musicEnabled preferences */
  useEffect(() => {
    // We rely on a promise tied to window object because we call this context multiple times per legacy page.
    // We don't want to slam the API that many times per page, so we bundle them up together.
    // TODO: react-refactor : get rid of this and put into a context.
    const globalPreferenceFetch = () => {
      if (!window.globalUserPreferencesFetch) {
        window.globalUserPreferencesFetch = new Promise((resolve, reject) => {
          fetch('/api/v2/users/preferences/music_enabled')
            .then((res) => res.json())
            .then((data) => resolve(data))
            .catch((response) => reject(response))
        })
      }

      return window.globalUserPreferencesFetch
        .then((data) => {
          if (shouldPlayMusic !== data) {
            setShouldPlayMusic(data)
            setPreferencesLoaded(true)
          }
        })
        .catch((res) => {
          // On error just auto-play music.
          setShouldPlayMusic(true)
          setPreferencesLoaded(true)
        })
    }

    globalPreferenceFetch()
  }, [])

  /* Keeps range input in sync with state */
  const handleUserVolumeChange = (e) => {
    setUserVolume(e.target.value)
  }

  /* Persist user's musicPlay choice to state and user preferences. Used in MusicPlayerCard.js */
  const toggleShouldPlay = () => {
    axios.put('/preferences', {
      music_enabled: !shouldPlayMusic
    })
    setShouldPlayMusic(!shouldPlayMusic)
  }

  /* Used to hide the music card after the song title exits the marquee. Used in MusicMarquee.js */
  const handleHideMarquee = () => {
    setMarqueeExiting(true)
    setTimeout(() => setShowMarquee(false), 300)
  }

  /* Initialize and setup feedFM music player */
  useEffect(() => {
    const setupPlayer = async () => {
      try {
        // Get player token and secret from server.
        const res = await axios.get('music/token', {
          params: {
            version: '1'
          }
        })
        if (res && res.data && res.data.token && res.data.secret) {
          const player = new Feed.Player(res.data.token, res.data.secret)

          // initializeAudio() hooks music player into audio streams before doing anything else.
          // This prevents an audio hiccup on first play.
          if (player) {
            player.initializeAudio()
            player.tune()
            setMusicPlayer(player)
          }
        }
      } catch (e) {
        // Fail silently?
      }
    }

    setupPlayer()
  }, [])

  const updateCurrentSong = (event) => {
    const album = event.audio_file.release.title
    const artist = event.audio_file.artist.name
    const song = event.audio_file.track.title
    setCurrentSong(`${artist} · ${album} · ${song}`)
  }

  const skipMusic = () => {
    if (musicPlayer.maybeCanSkip()) {
      musicPlayer.skip()
      return true
    } else {
      return false
    }
  }

  /* Configure stationsObject and add Event listeners to keep currentSong updated */
  useEffect(() => {
    if (musicPlayer) {
      const configureStationsObject = (stations) => {
        const stationsObject = {}
        stations.forEach((station) => {
          station.options.stationID.forEach((id) => {
            stationsObject[id] = station.id
          })
        })
        setStationsObject(stationsObject)
      }

      // Attach event listeners to music player
      musicPlayer.on('play-started', updateCurrentSong)
      musicPlayer.on('play-resumed', updateCurrentSong)

      musicPlayer.on('stations', (stations) => {
        configureStationsObject(stations)
      })

      musicPlayer.on('play-stopped', () => {
        setCurrentSong(null)
      })

      musicPlayer.initializeAudio()
    }
  }, [musicPlayer])

  /* Pause music player if shouldPlayMusic toggled */
  useEffect(() => {
    if (musicPlayer && !shouldPlayMusic) {
      if (musicPlayer.getCurrentState() === 'playing') {
        musicPlayer.pause()
      }
    }
  }, [musicPlayer, shouldPlayMusic])

  /**
   *  Attach music player to video player to keep them in sync.
   *
   *  We do not want to play on unpause, as we want to ensure that the user's music is synced to the most-up-to-date music event;
   *  e.g. they pause, skip ahead, and then click play, we want to handle gracefully
   **/
  useEffect(() => {
    if (musicPlayer && videoPlayer) {
      videoPlayer.on('play', () => musicPlayer.initializeAudio())
      videoPlayer.on('pause', () => musicPlayer.pause())
      videoPlayer.on('chromecastConnected', () => {
        // When chromecast is going, stop the music playing.
        // We want to keep track of whether the user wants to autoplay music.
        chromeCastRef.current = shouldPlayMusic
        setShouldPlayMusic(false)
      })
      videoPlayer.on('chromecastDisconnected', () => {
        if (chromeCastRef.current) {
          setShouldPlayMusic(chromeCastRef.current)
        }
      })
    }
  }, [musicPlayer, videoPlayer])

  /* Sync debouncedVolume to musicPlayer, mitigated by eventVolume. */
  useEffect(() => {
    // We typeof check eventVolume here, because 0 is falsey.
    if (musicPlayer && debouncedUserVolume && typeof eventVolume === 'number') {
      // eventVolume {float} is a percentage of debouncedUserVolume {int}, so divide it by 100.
      const desiredVolume = debouncedUserVolume * (eventVolume / 100)
      musicPlayer.setVolume(desiredVolume)
    }
  }, [musicPlayer, debouncedUserVolume, eventVolume])

  /* Persist user's volume choice to local storage after debounce */
  useEffect(() => {
    if (debouncedUserVolume) {
      localStorage.setItem('musicVolume', debouncedUserVolume)
    }
  }, [debouncedUserVolume])

  /* Display music card when a song plays during a video view !! CONTRACTUAL REQUIREMENT !! */
  useEffect(() => {
    if (currentSong) {
      /* Reset musicCardShowing states */
      setMarqueeExiting(false)
      setShowMarquee(true)
    }
  }, [currentSong])

  // TODO: feedfm - make sure setStation works correctly
  /* Trigger a feedfm station change when the stationId changes */
  useEffect(() => {
    if (musicPlayer && stationId && stationsObject) {
      const feedStationId = stationsObject[stationId]
      if (feedStationId) {
        musicPlayer.setStationId(feedStationId)
      }
    }
  }, [musicPlayer, stationId, stationsObject])

  const handleChangeVolumeEvent = (event) => {
    const { startTime, endTime } = event
    const { stationId: eventStationId, startVolume, endVolume } = event.data

    // Sync station to changeVolumeEvent
    if (stationId !== eventStationId) {
      setStationId(eventStationId)
    }

    // Calculate and set eventVolume for changeVolumeEvent
    const stepAmount = (endVolume - startVolume) / (endTime - 1 - startTime)
    const currentStep = state.time - startTime
    const newVolume = currentStep * stepAmount + startVolume
    if (eventVolume !== newVolume) {
      setEventVolume(newVolume)
    }
    if (musicPlayer.getCurrentState() !== 'playing' && !videoPlayer.paused()) {
      musicPlayer.play()
    }
  }

  const handleFixedVolumeEvent = (event) => {
    const { stationId: eventStationId, volume } = event.data

    if (stationId !== eventStationId) {
      setStationId(eventStationId)
    }
    if (eventVolume !== volume) {
      setEventVolume(volume)
    }
    if (musicPlayer.getCurrentState() !== 'playing' && !videoPlayer.paused()) {
      musicPlayer.play()
    }
  }

  /* Check music event track whenever video's state.time updates */
  useEffect(() => {
    const checkEventTrack = () => {
      // We use for(let n of m) here so we can bail out early (unlike .forEach())
      for (let event of musicEvents) {
        // We walk the musicEvents array and the range on each event.
        // If we are within that range, run the function and then bail out early. Otherwise, keep looking.
        if (isInRange(state.time, event.startTime, event.endTime)) {
          switch (event.eventType) {
            case 'no_music': {
              if (musicPlayer.getCurrentState() !== 'idle') {
                musicPlayer.pause()
                setCurrentSong()
              }
              break
            }
            case 'change_volume': {
              handleChangeVolumeEvent(event)
              break
            }
            case 'fixed_volume': {
              handleFixedVolumeEvent(event)
              break
            }
            default: {
              break
            }
          }
        }
      }
    }

    if (musicPlayer && state.time && shouldPlayMusic && !videoPlayer.paused()) {
      checkEventTrack()
    }

    return () => {}
  }, [videoPlayer, musicEvents, musicPlayer, shouldPlayMusic, state.time])

  const contextValue = {
    currentSong,
    handleHideMarquee,
    handleUserVolumeChange,
    marqueeExiting,
    shouldPlayMusic,
    toggleShouldPlay,
    showMarquee,
    userVolume,
    skipMusic
  }

  return (
    <Store.Provider value={contextValue}>
      {preferencesLoaded && children}
    </Store.Provider>
  )
}

export default MusicContextProvider
